import React, { useEffect, useState } from 'react';
import { 
  Spin, 
  message, 
  Table, 
  Card, 
  Row, 
  Col,
  Space,
  Typography,
  Alert,
  theme
} from 'antd';
import { 
  UserOutlined, 
  GiftOutlined, 
  ShoppingCartOutlined, 
  DollarOutlined
} from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import { getDashboardStats } from './dashboardapi';
import { formateCoins } from '../../function';

const { Title, Text } = Typography;

const DashboardCards = () => {
  const { token } = theme.useToken();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (error) {
        message.error('Échec du chargement des données du dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (!loading && !stats) {
    return <Alert type="error" showIcon message="Unable to load dashboard data" description="Please refresh the page to try again." />;
  }
  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        padding: "50px", 
        marginBottom: "30px" 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Prepare achats data for 12 months
  const achatsData = Array(12)
    .fill(0)
    .map((_, i) => {
      const month = i + 1;
      const found = stats.achats_par_mois.find((item) => item.month === month);
      return { month: `Mois ${month}`, total: found ? found.total : 0 };
    });

  // Prepare ventes data for 12 months
  const ventesData = Array(12)
    .fill(0)
    .map((_, i) => {
      const month = i + 1;
      const found = stats.ventes_par_mois.find((item) => item.month === month);
      return { month: `Mois ${month}`, total: found ? found.total : 0 };
    });

  // Columns for Top Offres table
 // Columns for Top Offres table
const columns = [
  {
    title: 'Offre',
    dataIndex: 'coins',
    key: 'coins',
    render: (coins, record) => (
      <Text style={{ color: "var(--primary-color)" }}>
        {formateCoins(coins)} coins - {record.montant} USD
      </Text>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    render: (type) => (
      <Text style={{ color: "var(--primary-color3)" }}>
        {type?.nom || 'N/A'}
      </Text>
    ),
  },
  {
    title: 'Achats',
    dataIndex: 'achats_count',
    key: 'achats_count',
    sorter: (a, b) => a.achats_count - b.achats_count,
    defaultSortOrder: 'descend',
    render: (text) => (
      <Text style={{ color: "var(--primary-color)", fontWeight: 'bold' }}>
        {text}
      </Text>
    ),
  },
];


  // Chart config for achats
  const achatsConfig = {
    data: achatsData,
    xField: 'month',
    yField: 'total',
    smooth: true,
    theme: token.colorBgContainer === '#191f19' ? 'dark' : 'light',
    color: token.colorPrimary,
    point: { 
      size: 5, 
      shape: 'diamond',
      style: {
        fill: token.colorPrimary,
        stroke: token.colorPrimary,
      }
    },
    lineStyle: {
      stroke: token.colorPrimary,
    },
    tooltip: { 
      showMarkers: false,
    },
    height: 300,
  };

  // Chart config for ventes
  const ventesConfig = {
    data: ventesData,
    xField: 'month',
    yField: 'total',
    smooth: true,
    theme: token.colorBgContainer === '#191f19' ? 'dark' : 'light',
    color: token.colorPrimary,
    point: { 
      size: 5, 
      shape: 'diamond',
      style: {
        fill: token.colorPrimary,
        stroke: token.colorPrimary,
      }
    },
    lineStyle: {
      stroke: token.colorPrimary,
    },
    tooltip: { 
      showMarkers: false,
    },
    height: 300,
  };

  return (
    <div className="admin-overview" style={{ 
      padding: '24px',
      backgroundColor: "var(--background-body)",
      minHeight: '100vh',
      color: "var(--primary-color)"
    }}>
      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Title 
            level={2} 
            style={{ 
              color: "var(--primary-color)",
              margin: 0
            }}
          >
            Tableau de Bord
          </Title>
        </Space>
      </div>

      {/* Cartes de statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card 
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ textAlign: 'center' }}>
              <UserOutlined style={{ 
                fontSize: '32px', 
                color: "var(--primary-color3)",
                marginBottom: '12px'
              }} />
              <Text style={{ 
                display: 'block',
                color: "var(--primary-color)",
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Clients
              </Text>
              <Title 
                level={3} 
                style={{ 
                  color: "var(--primary-color)",
                  margin: 0,
                  fontSize: '28px'
                }}
              >
                {stats.total_clients || 0}
              </Title>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} xl={6}>
          <Card 
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ textAlign: 'center' }}>
              <GiftOutlined style={{ 
                fontSize: '32px', 
                color: "var(--primary-color3)",
                marginBottom: '12px'
              }} />
              <Text style={{ 
                display: 'block',
                color: "var(--primary-color)",
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Offres
              </Text>
              <Title 
                level={3} 
                style={{ 
                  color: "var(--primary-color)",
                  margin: 0,
                  fontSize: '28px'
                }}
              >
                {stats.total_offres || 0}
              </Title>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} xl={6}>
          <Card 
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ textAlign: 'center' }}>
              <ShoppingCartOutlined style={{ 
                fontSize: '32px', 
                color: "var(--primary-color3)",
                marginBottom: '12px'
              }} />
              <Text style={{ 
                display: 'block',
                color: "var(--primary-color)",
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Achats
              </Text>
              <Title 
                level={3} 
                style={{ 
                  color: "var(--primary-color)",
                  margin: 0,
                  fontSize: '28px'
                }}
              >
                {stats.total_achats || 0}
              </Title>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} xl={6}>
          <Card 
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ textAlign: 'center' }}>
              <DollarOutlined style={{ 
                fontSize: '32px', 
                color: "var(--primary-color3)",
                marginBottom: '12px'
              }} />
              <Text style={{ 
                display: 'block',
                color: "var(--primary-color)",
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Ventes
              </Text>
              <Title 
                level={3} 
                style={{ 
                  color: "var(--primary-color)",
                  margin: 0,
                  fontSize: '28px'
                }}
              >
                {stats.total_ventes || 0}
              </Title>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Graphiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Text strong style={{ color: "var(--primary-color)" }}>
                Achats par mois (année en cours)
              </Text>
            }
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Line {...achatsConfig} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Text strong style={{ color: "var(--primary-color)" }}>
                Ventes par mois (année en cours)
              </Text>
            }
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Line {...ventesConfig} />
          </Card>
        </Col>
      </Row>

      {/* Tableau Top Offres */}
      <Row>
        <Col xs={24}>
          <Card 
            title={
              <Text strong style={{ color: "var(--primary-color)" }}>
                Top 5 des offres les plus achetées
              </Text>
            }
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              dataSource={stats.top_offres}
              columns={columns}
              pagination={false}
              rowKey="id"
              size="middle"
              style={{ 
                backgroundColor: "var(--background-body)",
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardCards;
